import { useQuery } from '@apollo/react-hooks';
import KeyboardArrowDownOutlinedIcon from '@material-ui/icons/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlined from '@material-ui/icons/KeyboardArrowUpOutlined';
import React, { memo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import {
  GetUpdatedUserHistory,
  GetUpdatedUserHistoryVariables,
} from '../../../../generated/GetUpdatedUserHistory';
import { StatusVerify } from '../../../../generated/globalTypes';
import { VerificationRequests_requestVerificationAdvance_row as Data } from '../../../../generated/VerificationRequests';
import { getFormattedSentenceDate } from '../../../../helpers/formatDate';
import { colors } from '../../constants/theme';
import { useSnackbar } from '../../context';
import { Text } from '../../core-ui';
import { GET_UPDATED_USER_HISTORY } from '../../graphql/queries';

import { Collapsible } from './Collapsible';
import CollapsedItem from './OwnerCollapsedItem';
import { OwnerLogChanges } from './types';
import OwnerDataForm from './OwnerDataForm';
import OwnerEditForm from './OwnerEditForm';

type Props = {
  data: Data;
  viewMode?: boolean;
  refetchData: () => void;
  onClose: () => void;
};

const OwnerSuperEditForm = memo((props: Props) => {
  const { data, refetchData, onClose } = props;
  const [selectedTab, setSelectedTab] = useState('FORM');

  const { openSnackbar } = useSnackbar();

  const { data: historyData, refetch: refetchHistory } = useQuery<
    GetUpdatedUserHistory,
    GetUpdatedUserHistoryVariables
  >(GET_UPDATED_USER_HISTORY, {
    variables: { verificationId: data.id },
    fetchPolicy: 'network-only',
  });

  const HeaderCollapsible = ({
    title,
    changes,
    collapsed,
  }: {
    title: string;
    changes: number;
    collapsed: boolean;
  }) => {
    return (
      <View style={styles.headerContainer}>
        <Text style={styles.titleText}>{title}</Text>
        <View style={styles.headerText}>
          <Text>{changes.toString()} perubahan</Text>
          {collapsed ? (
            <KeyboardArrowDownOutlinedIcon />
          ) : (
            <KeyboardArrowUpOutlined />
          )}
        </View>
      </View>
    );
  };

  return (
    <>
      <View style={styles.root}>
        <View style={styles.header}>
          <View style={styles.row}>
            <TouchableOpacity
              data-testid="list-daftar-data"
              style={[
                styles.tab,
                selectedTab === 'FORM' ? styles.selectedTab : {},
              ]}
              onPress={() => setSelectedTab('FORM')}
            >
              <Text
                color={selectedTab === 'FORM' ? 'black' : 'grey'}
                bold
                size="m"
              >
                {'Form'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              data-testid="list-dalam-masa-pengerjaan"
              style={[
                styles.tab,
                selectedTab === 'RIWAYAT' ? styles.selectedTab : {},
              ]}
              onPress={() => setSelectedTab('RIWAYAT')}
            >
              <Text
                color={selectedTab === 'RIWAYAT' ? 'black' : 'grey'}
                bold
                size="m"
              >
                {t(['Riwayat', 'History'])}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
        {selectedTab === 'FORM' ? (
          data.status === StatusVerify.WAITING ? (
            <OwnerEditForm
              viewMode
              data={data}
              onClose={onClose}
              refetchProfile={() => {
                refetchData();
                refetchHistory();
              }}
            />
          ) : (
            <OwnerDataForm
              onUpdate={() => {
                refetchData();
                refetchHistory();
                openSnackbar({
                  variant: 'success',
                  message: t([
                    'Data telah diperbarui.',
                    'Data has been updated.',
                  ]),
                });
                onClose();
              }}
              isMasterAdminRole={true} //Only in Aldystira as Master Verif Admin
              isSuperEdit={true} //Only in Aldystira as Master Verif Admin
              data={data}
            />
          )
        ) : (
          <>
            {historyData?.getUpdatedUserHistory.map(
              ({ createdAt, currentData, updatedAttributes }) => {
                let returned: OwnerLogChanges = JSON.parse(
                  updatedAttributes || '',
                );
                let count = 0;
                returned.birthDate && count++;
                returned.email && count++;
                returned.gender && count++;
                returned.name && count++;
                returned.phoneNumber && count++;
                returned.googleSheetFormNumber && count++;
                returned.ownerVerified && count++;
                // console.log(returned);

                return (
                  <View style={styles.collapseContainer} key={createdAt}>
                    <Collapsible
                      onPressClosing={() => {}}
                      onPressOpening={() => {}}
                      title={
                        <HeaderCollapsible
                          changes={count}
                          collapsed={true}
                          title={getFormattedSentenceDate(new Date(createdAt))}
                        />
                      }
                      titleOpened={
                        <HeaderCollapsible
                          changes={count}
                          collapsed={false}
                          title={getFormattedSentenceDate(new Date(createdAt))}
                        />
                      }
                    >
                      <CollapsedItem
                        currentData={currentData || ''}
                        updatedData={updatedAttributes || ''}
                      />
                    </Collapsible>
                  </View>
                );
              },
            )}
          </>
        )}
      </View>
    </>
  );
});

const styles = StyleSheet.create({
  header: {
    backgroundColor: colors.background.white,
    flexDirection: 'row',
    height: 40,
    shadowColor: colors.background.shadow,
  },
  // headerTitle: { alignItems: 'center', flexDirection: 'row' },
  root: { width: 529, height: 1000, backgroundColor: colors.background.white },
  row: { flexDirection: 'row' },
  tab: {
    justifyContent: 'flex-start',
    height: '100%',
    marginHorizontal: 12,
  },
  selectedTab: {
    borderBottomWidth: 2,
    borderBottomColor: colors.button.background.primary,
  },
  titleText: { fontWeight: 'bold' },
  headerContainer: {
    backgroundColor: colors.background.white,
    borderColor: colors.border.primary,
    padding: 16,
    borderWidth: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
  },
  headerText: { flexDirection: 'row', alignItems: 'center' },
  collapseContainer: { marginTop: 16 },
});

export default OwnerSuperEditForm;
